import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bowfast/ui/components/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bowfast/ui/components/dialog";
import { Progress } from "@bowfast/ui/components/progress";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@bowfast/ui/components/select";
import { cn } from "@bowfast/ui/lib/utils";
import { motion } from "framer-motion";
import {
  CheckCircle2Icon,
  ClockIcon,
  PlusIcon,
  SailboatIcon,
  ShieldCheckIcon,
  Trash2Icon,
  UserPlusIcon,
  UsersIcon,
  XCircleIcon,
} from "lucide-react";
import { useState } from "react";
import type { CrewJoinType, CrewRole } from "~/graphql/generated";
import { fadeUp, stagger } from "~/lib/animations";
import { CREW_ROLE_LABEL } from "~/lib/expedition-constants";
import { nameInitials } from "~/lib/expedition-utils";
import { gradientForId } from "~/lib/gradients";
import { type AssessmentResult, RESULT_CONFIG } from "~/lib/skills";
import { VESSEL_GRADIENTS } from "~/pages/expeditions/expedition-detail.mock";
import type { ExpeditionCrewMember, ExpeditionVesselAssignment } from "~/pages/expeditions/expeditions.types";

// TODO(backend/crew): roleClass will be replaced by roleCls from ~/lib/expedition-utils
// once Step 1 (remove inline infrastructure) is completed.
function roleClass(role: CrewRole): string {
  if (role === "SKIPPER") return "bg-abyss-50 text-abyss-600 dark:bg-abyss-900";
  if (role === "FIRST_MATE") return "bg-violet-50 text-violet-600 dark:bg-violet-900";
  if (role === "STUDENT") return "bg-signal-50 text-signal-700 dark:bg-signal-900";
  if (role === "OBSERVER") return "bg-muted text-muted-foreground";
  return "bg-shallow-50 text-shallow-700 dark:bg-shallow-900";
}

const JOIN_TYPE_LABEL: Record<CrewJoinType, string> = {
  INVITED: "Invited",
  PAID: "Paid berth",
  COMPLIMENTARY: "Complimentary",
};

// ── Crew assessments ──────────────────────────────────────────────────────────
// TODO(backend/assessments): replace CREW_ASSESSMENTS mock with an
// ExpeditionCrewAssessments($expeditionId) query once the backend exposes
// per-expedition assessments per crew member (linked via CrewMembership ID).

interface ExpeditionAssessment {
  id: string;
  skillName: string;
  category: string;
  result: AssessmentResult;
  ratingLabel: string;
  assessor: string;
  date: string;
  notes?: string;
}

const CREW_ASSESSMENTS: Record<string, ExpeditionAssessment[]> = {
  "1": [
    {
      id: "ca1",
      skillName: "Passage planning",
      category: "Navigation",
      result: "passed",
      ratingLabel: "Excellent (5)",
      assessor: "Sofia Carvalho",
      date: "12 Nov 2026",
    },
    {
      id: "ca2",
      skillName: "Chart reading & interpretation",
      category: "Navigation",
      result: "passed",
      ratingLabel: "Good (4)",
      assessor: "Sofia Carvalho",
      date: "12 Nov 2026",
      notes: "Struggled slightly with Admiralty symbols but recovered well",
    },
  ],
  "2": [
    {
      id: "ca3",
      skillName: "Heavy weather sailing",
      category: "Seamanship",
      result: "partial",
      ratingLabel: "In progress",
      assessor: "James Hunt",
      date: "14 Nov 2026",
    },
  ],
};

// ── Assessment dialog ─────────────────────────────────────────────────────────

function CrewAssessmentDialog({
  member,
  assessments,
  open,
  onOpenChange,
}: Readonly<{
  member: { name: string; initials: string };
  assessments: ExpeditionAssessment[];
  open: boolean;
  onOpenChange: (v: boolean) => void;
}>) {
  const passed = assessments.filter((a) => a.result === "passed").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheckIcon className="size-4 text-abyss-500" />
            {member.name} — skill assessments
          </DialogTitle>
          <DialogDescription>
            Assessments recorded on this expedition.
            {assessments.length > 0 && ` ${passed} of ${assessments.length} passed.`}
          </DialogDescription>
        </DialogHeader>

        {assessments.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No assessments recorded for this crew member yet.
          </div>
        ) : (
          <div className="space-y-0">
            {assessments.map((a) => {
              const cfg = RESULT_CONFIG[a.result];
              return (
                <div key={a.id} className="flex items-start gap-3 border-b border-border py-3 last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{a.skillName}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {a.category} · {a.assessor} · {a.date}
                    </p>
                    {a.notes && <p className="mt-1 text-xs italic text-muted-foreground">"{a.notes}"</p>}
                  </div>
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-[0.04em]",
                      cfg.className,
                    )}
                  >
                    {cfg.label} · {a.ratingLabel}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {assessments.length > 0 && (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
            <Progress value={Math.round((passed / assessments.length) * 100)} className="h-1.5 flex-1" />
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {passed}/{assessments.length} passed
            </span>
          </div>
        )}

        <div className="flex justify-end">
          <Button size="sm" className="gap-2">
            <PlusIcon className="size-3.5" />
            Record assessment
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Crew table row ────────────────────────────────────────────────────────────

function CrewTableRow({
  member,
  isMultiVessel,
  vessels,
  onUpdateRole,
  onRemove,
  isMutating,
}: Readonly<{
  member: ExpeditionCrewMember;
  isMultiVessel: boolean;
  vessels: ExpeditionVesselAssignment[];
  onUpdateRole?: (membershipId: string, role: CrewRole) => void;
  onRemove?: (membershipId: string) => void;
  isMutating?: boolean;
}>) {
  const vessel = isMultiVessel ? vessels.find((v) => v.vessel.id === member.vesselId) : undefined;
  const displayName = member.user.displayName || `${member.user.firstName} ${member.user.lastName}`;
  const initials = nameInitials(displayName);
  const joinLabel = JOIN_TYPE_LABEL[member.joinType];
  const [assessOpen, setAssessOpen] = useState(false);
  // TODO(backend/assessments): CREW_ASSESSMENTS is keyed by mock IDs.
  // Replace with real query keyed by member.id when backend supports it.
  const memberAssessments = CREW_ASSESSMENTS[member.id] ?? [];

  return (
    <div
      className={cn(
        "grid items-center gap-2 border-b border-border px-4 py-2.5 last:border-b-0 transition-colors hover:bg-muted/20",
        isMultiVessel
          ? "grid-cols-[1fr_7.5rem_8rem_5rem_4rem_3rem_2rem_2rem]"
          : "grid-cols-[1fr_7.5rem_5rem_4rem_3rem_2rem_2rem]",
      )}
    >
      {/* Member */}
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-lg bg-linear-to-br font-heading text-[0.6875rem] font-bold text-white",
            gradientForId(member.id),
          )}
          aria-hidden
        >
          {initials}
        </div>
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 truncate text-sm font-medium">
            {displayName}
            {/* TODO(backend/crew): add isOwner / isCurrentUser flag to CrewMembership */}
          </p>
          <p className="truncate text-xs text-muted-foreground">{member.user.email}</p>
        </div>
      </div>
      {/* Role — inline select */}
      <Select
        defaultValue={member.role}
        onValueChange={(val) => onUpdateRole?.(member.id, val as CrewRole)}
        disabled={isMutating}
      >
        <SelectTrigger
          size="sm"
          className="h-auto w-fit gap-0.5 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 [&>svg]:size-3 [&>svg]:text-muted-foreground/40"
        >
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-label",
              roleClass(member.role),
            )}
          >
            <SelectValue />
          </span>
        </SelectTrigger>
        <SelectContent align="start">
          <SelectGroup>
            {(Object.keys(CREW_ROLE_LABEL) as CrewRole[]).map((key) => (
              <SelectItem key={key} value={key}>
                {CREW_ROLE_LABEL[key]}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      {/* Vessel — inline select (multi-vessel only) */}
      {isMultiVessel && (
        <Select defaultValue={member.vesselId ?? ""}>
          <SelectTrigger
            size="sm"
            className="h-auto w-fit gap-0.5 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 [&>svg]:size-3 [&>svg]:text-muted-foreground/40"
          >
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              {vessel && <span>{vessel.vessel.name}</span>}
              <SelectValue />
            </span>
          </SelectTrigger>
          <SelectContent align="start">
            <SelectGroup>
              {vessels.map((v) => (
                <SelectItem key={v.id} value={v.vessel.id}>
                  {/* TODO(backend/vessels): add `flag` emoji to Vessel type */}
                  {v.vessel.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      )}
      {/* Join type */}
      <span className="text-xs text-muted-foreground">{joinLabel}</span>
      {/* Miles */}
      <span className="text-right text-sm font-medium tabular-nums">
        {member.user.totalMilesLogged > 0 ? member.user.totalMilesLogged.toLocaleString() : "—"}
      </span>
      {/* Paid — TODO(backend/crew): add `paid`/`settledAt` to CrewMembership; showing "—" until then */}
      <span className="flex justify-center">
        <span className="text-xs text-muted-foreground">—</span>
      </span>
      {/* Shield / assessments */}
      <Button
        variant="ghost"
        size="icon-sm"
        title="View skill assessments"
        onClick={() => setAssessOpen(true)}
        className={cn(memberAssessments.length > 0 && "text-abyss-500")}
      >
        <ShieldCheckIcon className="size-3.5" />
      </Button>
      {/* Remove crew member */}
      {onRemove && (
        <Button
          variant="ghost"
          size="icon-sm"
          title="Remove from expedition"
          disabled={isMutating}
          onClick={() => onRemove(member.id)}
          className="text-muted-foreground hover:text-destructive"
        >
          <Trash2Icon className="size-3.5" />
        </Button>
      )}

      <CrewAssessmentDialog
        member={{ name: displayName, initials }}
        assessments={memberAssessments}
        open={assessOpen}
        onOpenChange={setAssessOpen}
      />
    </div>
  );
}

// ── CrewTab ───────────────────────────────────────────────────────────────────

export function CrewTab({
  crew,
  vessels,
  onApprove,
  onDecline,
  onRemove,
  onUpdateRole,
  isMutating,
}: Readonly<{
  crew: ExpeditionCrewMember[];
  vessels: ExpeditionVesselAssignment[];
  onApprove?: (membershipId: string) => void;
  onDecline?: (membershipId: string) => void;
  onRemove?: (membershipId: string) => void;
  onUpdateRole?: (membershipId: string, role: CrewRole) => void;
  isMutating?: boolean;
}>) {
  const confirmed = crew.filter((m) => m.status === "CONFIRMED");
  const pending = crew.filter((m) => m.status === "PENDING" || m.status === "WAITLISTED");
  const isMultiVessel = vessels.length > 1;
  const hasObserver = crew.some((m) => m.role === "OBSERVER");

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-5">
      {/* Multi-vessel legend */}
      {isMultiVessel && (
        <motion.div variants={fadeUp}>
          <div className="flex flex-wrap gap-3">
            {vessels.map((v, vi) => {
              const count = confirmed.filter((m) => m.vesselId === v.vessel.id).length;
              const grad = VESSEL_GRADIENTS[vi % VESSEL_GRADIENTS.length] ?? VESSEL_GRADIENTS[0];
              return (
                <div key={v.id} className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
                  <div
                    className={cn(
                      "flex size-9 shrink-0 items-center justify-center rounded-xl bg-linear-to-br p-0.5",
                      grad,
                    )}
                  >
                    <div className="flex size-full items-center justify-center rounded-[7px] bg-abyss-900">
                      <SailboatIcon className="size-3.5 text-white/80" />
                    </div>
                  </div>
                  <div>
                    <p className="font-heading text-sm font-bold">
                      {/* TODO(backend/vessels): add `flag` emoji to Vessel type */}
                      {v.vessel.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {/* TODO(backend/vessels): add `vesselType` display label + `maxCrew` (berths) to Vessel */}
                      {count} crew confirmed
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Confirmed crew table */}
      <motion.div variants={fadeUp}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <UsersIcon className="size-4 text-abyss-500" />
                Confirmed crew
                <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-[0.625rem] font-bold text-muted-foreground">
                  {confirmed.length}
                </span>
              </CardTitle>
              {/* TODO(backend/crew): wire InviteCrew mutation when user search is available */}
              <Button size="sm" variant="outline" className="gap-1.5">
                <UserPlusIcon className="size-3.5" />
                Invite crew
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Column headers */}
            <div
              className={cn(
                "grid gap-2 border-b border-border px-4 py-2 text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground",
                isMultiVessel
                  ? "grid-cols-[1fr_7.5rem_8rem_5rem_4rem_3rem_2rem_2rem]"
                  : "grid-cols-[1fr_7.5rem_5rem_4rem_3rem_2rem_2rem]",
              )}
            >
              <span>Crew member</span>
              <span>Role</span>
              {isMultiVessel && <span>Vessel</span>}
              <span>Join</span>
              <span className="text-right">Miles</span>
              {/* TODO(backend/crew): Paid column — add `paid` field to CrewMembership */}
              <span className="text-center">Paid</span>
              <span />
              <span />
            </div>
            {confirmed.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground">No confirmed crew yet.</p>
            ) : (
              confirmed.map((m) => (
                <CrewTableRow
                  key={m.id}
                  member={m}
                  isMultiVessel={isMultiVessel}
                  vessels={vessels}
                  {...(onUpdateRole ? { onUpdateRole } : {})}
                  {...(onRemove ? { onRemove } : {})}
                  {...(isMutating !== undefined ? { isMutating } : {})}
                />
              ))
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Pending section */}
      {pending.length > 0 && (
        <motion.div variants={fadeUp}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <ClockIcon className="size-4 text-signal-500" />
                Pending applications
                <span className="ml-1 rounded-full bg-signal-50 px-2 py-0.5 text-[0.625rem] font-bold text-signal-700 dark:bg-signal-950 dark:text-signal-300">
                  {pending.length}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {pending.map((m) => {
                const displayName = m.user.displayName || `${m.user.firstName} ${m.user.lastName}`;
                const initials = nameInitials(displayName);
                return (
                  <div
                    key={m.id}
                    className="flex items-center gap-4 border-b border-border px-5 py-3 last:border-b-0 transition-colors hover:bg-muted/20"
                  >
                    <div
                      className={cn(
                        "flex size-8 shrink-0 items-center justify-center rounded-lg bg-linear-to-br font-heading text-[0.6875rem] font-bold text-white",
                        gradientForId(m.id),
                      )}
                      aria-hidden
                    >
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{displayName}</p>
                      <p className="text-xs text-muted-foreground">{m.user.email}</p>
                    </div>
                    <span className="rounded-full bg-signal-50 px-2 py-0.5 text-[0.625rem] font-bold text-signal-700 dark:bg-signal-950 dark:text-signal-300">
                      {m.status === "WAITLISTED" ? "Waitlisted" : "Pending"}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        disabled={isMutating}
                        onClick={() => onApprove?.(m.id)}
                      >
                        <CheckCircle2Icon className="size-3.5" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-signal-600"
                        disabled={isMutating}
                        onClick={() => onDecline?.(m.id)}
                      >
                        <XCircleIcon className="size-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Observer notice */}
      {hasObserver && (
        <motion.div variants={fadeUp}>
          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
            <ShieldCheckIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              Observers are non-participating crew members who join for experience. They are not eligible for sailing
              miles or skill assessments on this expedition.
            </p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
